import TokenHandler from "./TokenHandler";
import StorageItem from "../../StorageItem";
import {
	Information,
	PortalAuthTimeout,
	SessionToken,
	SessionTokenInterface
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import AbstractContext from "../../AbstractContext";
import AbstractSession from "../AbstractSession";
import {JWTPayload} from "jose/dist/types/types";
import * as jose from "jose";
import FunctionHelper from "../../helper/FunctionHelper";
import DateHelper from "../../helper/DateHelper";
import TimeUnit from "../../units/TimeUnit";

/**
 * A AbstractTokenHandler offers the default implementations for the token handling of server tokens used in an
 * {@link AbstractSession}.
 */
export default class AbstractTokenHandler<C extends AbstractContext<C, S>, S extends AbstractSession<C, S>>
	implements TokenHandler {
	protected context: C;

	/**
	 * Constructs an {@link AbstractTokenHandler} instance
	 */
	constructor(context: C) {
		this.context = context;
	}

	/**
	 * Checks if the refresh token found in {@link localStorage} is not expired and valid
	 */
	protected checkRefreshToken(): boolean {
		let refreshTokenCreation: string | null = localStorage.getItem(StorageItem.REFRESH_TOKEN_CREATION);
		let portalInfo: Information = this.context.info;

		if (FunctionHelper.exists(refreshTokenCreation && FunctionHelper.exists(portalInfo.serverStart))) {
			return parseInt(refreshTokenCreation!) > portalInfo.serverStart!
		}

		return true;
	}

	/**
	 * Clears all set session tokens from the current {@link AbstractSession} and shows the login dialog.
	 */
	protected async clear(): Promise<void> {
		let session: S = this.context.session;

		// clear login leftovers
		await session.clearSessionToken();
		this.context.getPage().setState({
			showLoginDialog: true,
			loginParameter: undefined
		});
	}

	/**
	 * Initializes an {@link AbstractTokenHandler} validating tokens from {@link localStorage} and
	 * {@link sessionStorage}.
	 */
	public async initialize(): Promise<void> {
		let session: S = this.context.session;
		let refreshToken: string | null = localStorage.getItem(StorageItem.REFRESH_TOKEN);
		let accessToken: string | null = sessionStorage.getItem(StorageItem.ACCESS_TOKEN);

		if (!this.checkRefreshToken()) {
			await this.clear();
			return;
		}

		if (accessToken !== null) {
			session.setToken(SessionToken.fromJson({
				token: accessToken,
				refreshToken: refreshToken !== null ? refreshToken : undefined
			} as SessionTokenInterface));
			await this.parseAccessToken(accessToken);
			await this.context.session.fetchUser();
			await this.context.session.getDocumentManager().sync();
		} else if (refreshToken !== null) {
			await session.refresh(refreshToken, true);
		} else {
			await this.clear();
		}
	}

	/**
	 * Parses the given JWT access token, checks for expiration (refreshing it if needed) and starts the refresh timeout
	 * of the associated {@link AbstractSession}.
	 */
	protected async parseAccessToken(accessToken: string): Promise<void> {
		let session: S = this.context.session;
		let token: SessionToken | undefined = session.getToken();

		let tokenPayload: JWTPayload = jose.decodeJwt(accessToken);

		let currentDate: Date = new Date();
		let expiryDate: Date = new Date();
		expiryDate.setTime(DateHelper.secondsToUnit(tokenPayload.exp!, TimeUnit.MILLISECOND));
		let expiresIn: number = Math.max(
			DateHelper.unitToSeconds(expiryDate.getTime() - currentDate.getTime(), TimeUnit.MILLISECOND), 0
		);

		if (expiresIn === 0) {
			if (FunctionHelper.exists(token?.refreshToken)) {
				await session.refresh(undefined, true);
			} else {
				session.clearSessionToken();
				window.location.reload();
			}
			return;
		}

		if (FunctionHelper.exists(token)) {
			token!.expiresIn = expiresIn;
			session.setToken(token!);
		}

		let refreshThreshold: number = FunctionHelper.applyValue(
			this.context.info.localAuth?.timeout?.autoRefresh, PortalAuthTimeout.getAutoRefreshDefault()
		);

		if (expiresIn < refreshThreshold) {
			if (FunctionHelper.exists(token?.refreshToken)) {
				await session.refresh(undefined, true);
			}
		}

		session.startTimeout();
	}
}